import React, { useEffect } from 'react';
import { Box, Container, Button } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';

const PageSection = styled(Box)`
  min-height: 100vh;
  padding: 120px 0 80px;
  background: linear-gradient(135deg, #000B18 0%, #001529 100%);
  color: #fff;
`;

const BackgroundPattern = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.2;
  z-index: 1;
`;

const Content = styled(Container)`
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

const Subtitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
  margin-bottom: 4rem;
`;

const JobGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const JobCard = styled(motion.div)`
  background: rgba(10, 25, 41, 0.5);
  border-radius: 24px;
  border: 1px solid rgba(79, 172, 254, 0.2);
  padding: 2.5rem;
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, border-color 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 380px;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at top left, rgba(79, 172, 254, 0.08), transparent 70%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(79, 172, 254, 0.4);
  }
`;

const JobTitle = styled.div`
  font-size: 2.5rem;
  color: #CCD6F6;
  margin-bottom: 2.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 1rem;
  line-height: 1.2;

  span {
    font-size: 2rem;
    margin-right: 0.5rem;
  }
`;

const JobDescription = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;

  li {
    color: #8892B0;
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    position: relative;

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: #4facfe;
    }

    &:last-child {
      margin-bottom: 2rem;
    }
  }
`;

const ApplyButton = styled(Button)`
  && {
    background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
    color: white;
    padding: 1rem;
    border-radius: 12px;
    text-transform: none;
    font-size: 1.2rem;
    font-weight: 500;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    width: 100%;
    margin-top: auto;
    border: none;

    &:hover {
      background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(79, 172, 254, 0.3);
    }
  }
`;

const Career = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jobs = [
    {
      title: '🔄 Full Stack Developer',
      responsibilities: [
        'Build end-to-end web applications across frontend and backend',
        'Integrate third-party services and APIs into our platform',
        'Collaborate across teams to deliver complete feature implementations'
      ]
    },
    {
      title: '📱 App Developer',
      responsibilities: [
        'Develop native and cross-platform mobile applications',
        'Implement responsive UI/UX for iOS and Android devices',
        'Integrate mobile apps with backend services and APIs'
      ]
    },
    {
      title: '🧠 Data Scientist',
      responsibilities: [
        'Develop advanced statistical models and machine learning algorithms',
        'Analyze complex datasets to extract actionable insights',
        'Lead the development of predictive analytics solutions'
      ]
    },
    {
      title: '🤖 AI Engineer',
      responsibilities: [
        'Design and implement AI solutions for our products',
        'Integrate natural language processing and computer vision capabilities',
        'Develop conversational agents and intelligent automation systems'
      ]
    },
    {
      title: '⚙️ ML Engineer',
      responsibilities: [
        'Build and deploy production-ready machine learning models',
        'Optimize model performance and scalability',
        'Create systems for continuous training and model monitoring'
      ]
    },
    {
      title: '📊 Data Analyst',
      responsibilities: [
        'Transform raw data into meaningful business insights',
        'Create interactive dashboards and data visualizations',
        'Support data-driven decision making across departments'
      ]
    },
    {
      title: '🌟 Internship Program',
      responsibilities: [
        'Gain hands-on experience in software development, data science, or AI',
        'Contribute to real-world projects under mentorship of senior team members',
        'Learn industry best practices and cutting-edge technologies'
      ]
    }
  ];

  const handleApply = () => {
    window.open('https://forms.gle/ByB1MhqoaBz7Tnjs5', '_blank');
  };

  return (
    <>
      <Navbar />
      <PageSection>
        <BackgroundPattern />
        <Content maxWidth="lg">
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Join Our Team
          </Title>
          <Subtitle>
            Help us shape the future of AI and transform industries
          </Subtitle>

          <JobGrid>
            {jobs.map((job, index) => (
              <JobCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <JobTitle>
                  {job.title.split(' ').map((part, i) => 
                    i === 0 ? <span key={i}>{part}</span> : ` ${part}`
                  )}
                </JobTitle>
                <JobDescription>
                  {job.responsibilities.map((resp, i) => (
                    <li key={i}>{resp}</li>
                  ))}
                </JobDescription>
                <ApplyButton
                  variant="contained"
                  onClick={handleApply}
                >
                  Apply Now
                </ApplyButton>
              </JobCard>
            ))}
          </JobGrid>
        </Content>
      </PageSection>
    </>
  );
};

export default Career; 